import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {StorageService} from './storage.service';
import {LoaderService} from './loader.service';
import {AlertService} from './alert.service';
import {NetworkService} from './network.service';
import {LocaldbService} from "./localdb.service";

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private serverURL = 'https://forta.sisdev.de/server/new/back.php';

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private alertService: AlertService,
    private loaderService: LoaderService,
    private networkService: NetworkService,
    private localDBService: LocaldbService,
  ) {}

  private async getHeader() {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };
  }

  private async getQueryData() {
    const q = {};
    const lng = await this.storageService.language();
    if(lng !== null) {
      q["language"] = lng;
    } else {
      q["language"] = 'en';
    }
    return q;
  }

  private async allowRequest() {
    return await this.networkService.deviceConnected();
  }

  public async getDisclaimer(): Promise<any> {
    if (await this.allowRequest()) {
      const headers = await this.getHeader();
      const q = await this.getQueryData();
      const postData = {};
      postData["ac"] = 'desc';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();

        if (result["status"] === 'ok') {
          return Promise.resolve(result["serverresult"][0]['desc']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async getAppHints(): Promise<any> {
    if (await this.allowRequest()) {
      const headers = await this.getHeader();
      const q = await this.getQueryData();
      const postData = {};
      postData["ac"] = 'apphints';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();
        if (result["status"] === 'ok') {
          return Promise.resolve(result["serverresult"][0]['apphints']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async getDataPrivacy(): Promise<any> {
    if (await this.allowRequest()) {
      const headers = await this.getHeader();
      const q = await this.getQueryData();
      const postData = {};
      postData["ac"] = 'dsgvo';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();
        if (result["status"] === 'ok') {
          return Promise.resolve(result["serverresult"][0]['dsgvo']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async getReferences(): Promise<any> {
    if (await this.allowRequest()) {
      const headers = await this.getHeader();
      const q = await this.getQueryData();
      const postData = {};
      postData["ac"] = 'creden';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();
        if (result["status"] === 'ok') {
          return Promise.resolve(result["serverresult"][0]['credentials']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async getAbout(): Promise<any> {
    if (await this.allowRequest()) {

      const headers = await this.getHeader();
      const q = await this.getQueryData();
      const postData = {};
      postData["ac"] = 'about';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();

        if (result["status"] === 'ok') {
          return Promise.resolve(result["serverresult"][0]['about']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async getMenu(): Promise<any> {
    if (await this.allowRequest()) {

      const headers = await this.getHeader();
      const q = await this.getQueryData();
      const postData = {};
      postData["ac"] = 'getmenu';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();

        if (result["status"] === 'ok') {
          await this.storageService.appMenu(result["serverresult"][0]);
          return Promise.resolve(result["serverresult"][0]);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async getLiveTables(tableName) {
    if (await this.allowRequest()) {
      let allowSync = true;

      // first thing check, if it's time to get the data
      const syncData = await this.localDBService.getSyncData();

      if (syncData !== null) {
        const now = new Date().getTime();

        // 1 day
        let temp = (syncData.config_sync_time + 86400000);
        if (temp > now) {
          allowSync = false;
        }

        // 12 hours
        temp = (syncData.config_sync_time + 43200000);
        if (temp > now) {
          allowSync = false;
        }

        // 12 hours
        temp = (syncData.config_sync_time + 43200000);
        if (temp > now) {
          allowSync = false;
        }
      }

      if (allowSync) {
        let action = '';
        if (tableName === 'icd') {
          action = 'geticdtables';
        } else if (tableName === 'atc') {
          action = 'getatctables';
        } else if (tableName === 'config') {
          action = 'getconfig';
        }

        if (action !== '') {
          const headers = await this.getHeader();
          const postData = {};
          postData["ac"] = action;

          try {
            const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();

            if (result["status"] === 'ok') {
              const res = result["serverresult"][0];

              if (tableName === 'icd') {
                this.localDBService.setICDTable(res['de'], 'de').then();
                this.localDBService.setICDTable(res['en'], 'en').then();
              } else if (tableName === 'atc') {
                this.localDBService.setATCTable(res['de'], 'de').then();
                this.localDBService.setATCTable(res['en'], 'en').then();
              } else if (tableName === 'config') {
                this.localDBService.setConfigTable(res['de'], 'de').then();
                this.localDBService.setConfigTable(res['en'], 'en').then();
              }
            }
          } catch (e) {}
        }
      } else {}
    }
  }

  public async indicationSearch(searchText): Promise<any> {
    if (await this.allowRequest()) {

      const headers = await this.getHeader();
      const q = await this.getQueryData();
      q['searchtext'] = searchText;
      q['searchtype'] = 1;

      const postData = {};
      postData["ac"] = 'searchd';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();

        if (result["status"] === 'ok') {
          return Promise.resolve(result['serverresult']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async substanceSearch(searchText): Promise<any> {
    if (await this.allowRequest()) {

      const headers = await this.getHeader();
      const q = await this.getQueryData();
      q['searchtext'] = searchText;
      q['searchtype'] = 5;

      const postData = {};
      postData["ac"] = 'searchd';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();
        if (result["status"] === 'ok') {
          return Promise.resolve(result['serverresult']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async detailsSearch(id, type): Promise<any> {
    if (await this.allowRequest()) {

      const headers = await this.getHeader();
      const q = await this.getQueryData();
      q['searchtext'] = id;
      q['searchtype'] = type;

      const postData = {};
      postData["ac"] = 'searchd';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();

        if (result["status"] === 'ok') {
          return Promise.resolve(result['serverresult']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async atcCodeSearch(searchText): Promise<any> {
    if (await this.allowRequest()) {

      const headers = await this.getHeader();
      const q = await this.getQueryData();
      q['searchtext'] = searchText;
      q['searchtype'] = 2;

      const postData = {};
      postData["ac"] = 'searchd';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();
        if (result["status"] === 'ok') {
          return Promise.resolve(result['serverresult']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

  public async icdCodeSearch(searchText): Promise<any> {
    if (await this.allowRequest()) {

      const headers = await this.getHeader();
      const q = await this.getQueryData();
      q['searchtext'] = searchText;
      q['searchtype'] = 3;

      const postData = {};
      postData["ac"] = 'searchd';
      postData["q"] = q;

      try {
        const result = await this.http.post(this.serverURL, postData, {headers}).toPromise();
        if (result["status"] === 'ok') {
          return Promise.resolve(result['serverresult']);
        } else {
          return Promise.reject(null);
        }
      } catch (e) {
        return Promise.reject(null);
      }
    } else {
      return Promise.reject(null);
    }
  }

}
